import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Map for the new-design store locator. Pins follow the list: the hovered
// or selected store's pin grows and turns dark, a pin click selects its
// store, and selecting a store flies the map to it. Same Esri street tiles
// as the classic map (no API key, Latin labels).

export interface StorePin {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

const DHAKA: [number, number] = [23.8103, 90.4125];

const pin = (state: 'idle' | 'hover' | 'active') => {
  const s = state === 'idle' ? 30 : 40;
  const fill = state === 'active' ? '#1c1a1b' : '#8a1c2b';
  return L.divIcon({
    className: 'v2pin' + (state !== 'idle' ? ' up' : ''),
    html: `<svg viewBox="0 0 24 24" width="${s}" height="${s}"><path fill="${fill}" stroke="#fff" stroke-width="1.2" d="M12 1.5c-4.4 0-8 3.4-8 7.8 0 5.6 7 12.6 7.3 12.9a1 1 0 0 0 1.4 0c.3-.3 7.3-7.3 7.3-12.9 0-4.4-3.6-7.8-8-7.8z"/><circle cx="12" cy="9.3" r="3" fill="#fff"/></svg>`,
    iconSize: [s, s],
    iconAnchor: [s / 2, s - 1],
  });
};
const ICONS = { idle: pin('idle'), hover: pin('hover'), active: pin('active') };
const ME = L.divIcon({ className: 'v2me', html: '<span></span>', iconSize: [18, 18], iconAnchor: [9, 9] });

// Re-fits when the stores change, and when the map is shown again on phones
// (it is created hidden there, so its first fit has no size to work with).
function Fit({ points, tick }: { points: [number, number][]; tick?: unknown }) {
  const map = useMap();
  const key = JSON.stringify(points) + String(tick);
  useEffect(() => {
    map.invalidateSize();
    if (!points.length) map.setView(DHAKA, 11);
    else if (points.length === 1) map.setView(points[0], 15);
    else map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

function FlyTo({ to }: { to: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (to) map.flyTo(to, Math.max(map.getZoom(), 15), { duration: 0.8 });
  }, [to, map]);
  return null;
}

function Resize({ tick }: { tick: unknown }) {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 60);
    return () => window.clearTimeout(id);
  }, [tick, map]);
  return null;
}

export default function StoreMap({
  pins,
  selected,
  hovered,
  me,
  onSelect,
  resizeKey,
}: {
  pins: StorePin[];
  selected: string | null;
  hovered: string | null;
  me: [number, number] | null;
  onSelect: (id: string) => void;
  resizeKey?: unknown;
}) {
  const points = useMemo(() => pins.map((p) => [p.lat, p.lng] as [number, number]), [pins]);
  const sel = pins.find((p) => p.id === selected);
  const flyTarget = useMemo<[number, number] | null>(() => (sel ? [sel.lat, sel.lng] : null), [sel]);

  return (
    <MapContainer center={DHAKA} zoom={11} scrollWheelZoom={false} zoomControl style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom"
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
        maxZoom={19}
      />
      <Fit points={me ? [...points, me] : points} tick={resizeKey} />
      <FlyTo to={flyTarget} />
      <Resize tick={resizeKey} />
      {pins.map((p) => (
        <Marker
          key={p.id}
          position={[p.lat, p.lng]}
          icon={p.id === selected ? ICONS.active : p.id === hovered ? ICONS.hover : ICONS.idle}
          zIndexOffset={p.id === selected ? 1000 : p.id === hovered ? 500 : 0}
          title={p.name}
          eventHandlers={{ click: () => onSelect(p.id) }}
        />
      ))}
      {me && <Marker position={me} icon={ME} interactive={false} />}
    </MapContainer>
  );
}
